/**
 * Subscriptions
 *
 * Manifold subscriptions for the selected clinic: summary, a renewal calendar,
 * and the list — paginated and searched on the server, so the page loads one
 * page of rows rather than every subscription. Each row opens the patient in
 * Odin (core patient matched by email, see services/manifoldSubscriptions) and
 * the customer in Hyperswitch. Chargebee subscriptions (MDExam legacy and WL)
 * are a separate page.
 */

import { useState, useEffect, useCallback } from 'react'
import { manifoldAPI } from '../services/api'
import {
  Card, CardContent, CardHeader, CardTitle,
  Button
} from '../components/ui'
import {
  RefreshCw, Loader2, AlertCircle, Users, DollarSign, Search, X, ExternalLink, ChevronLeft, ChevronRight,
} from 'lucide-react'
import RenewalCalendar from '../components/subscriptions/RenewalCalendar'
import DrugSupplyRings from '../components/subscriptions/DrugSupplyRings'
import { odinPatientUrl } from '../lib/portalAuthEvents'

const PAGE_SIZE = 50

const STATUS_COLORS = {
  active: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400' },
  paused: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400' },
  cancelled: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400' },
  future: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400' },
}

function formatCurrency(cents) {
  if (cents === null || cents === undefined) return '-'
  const dollars = cents / 100
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: dollars % 1 === 0 ? 0 : 2 }).format(dollars)
}

function formatDate(timestamp) {
  if (!timestamp) return '-'
  return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function StatusBadge({ status }) {
  const colors = STATUS_COLORS[status] || { bg: 'bg-muted', text: 'text-muted-foreground' }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors.bg} ${colors.text}`}>
      {status}
    </span>
  )
}

export default function HsSubscriptionsPage() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [page, setPage] = useState(1)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const params = { page, limit: PAGE_SIZE }
      if (statusFilter !== 'all') params.status = statusFilter
      if (search) params.search = search
      const response = await manifoldAPI.getHsSubscriptions(params)
      if (response.data.success) {
        setData(response.data.data)
      }
    } catch (err) {
      console.error('[HS-SUBS] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [statusFilter, search, page])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleSearch = (e) => {
    e.preventDefault()
    setPage(1)
    setSearch(searchInput.trim())
  }

  const clearSearch = () => {
    setSearchInput('')
    setPage(1)
    setSearch('')
  }

  const pickStatus = (s) => {
    setPage(1)
    setStatusFilter(s)
  }

  const summary = data?.summary
  const subscriptions = data?.subscriptions || []
  const byPlan = data?.by_plan || []
  const byDrug = data?.by_drug || []
  const total = data?.total || 0
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Subscriptions</h1>
          <p className="text-sm text-muted-foreground mt-1">Billed through Manifold. Renewals ahead and behind, and every subscription.</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <Users className="w-3.5 h-3.5" /> Total
                </div>
                <div className="text-xl font-bold">{summary?.total || 0}</div>
              </CardContent>
            </Card>
            {summary?.status_distribution?.map(s => (
              <Card key={s.status}>
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                    <StatusBadge status={s.status} />
                  </div>
                  <div className="text-xl font-bold">{s.count}</div>
                </CardContent>
              </Card>
            ))}
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <DollarSign className="w-3.5 h-3.5" /> Monthly run rate
                </div>
                <div className="text-xl font-bold">{formatCurrency(summary?.mrr)}</div>
                <div className="text-xs text-muted-foreground">Multi-month plans spread evenly per month</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <DollarSign className="w-3.5 h-3.5" /> Billing next 30 days
                </div>
                <div className="text-xl font-bold">{formatCurrency(summary?.next30?.amount)}</div>
                <div className="text-xs text-muted-foreground">{(summary?.next30?.renewals || 0).toLocaleString()} renewals scheduled</div>
              </CardContent>
            </Card>
          </div>

          <RenewalCalendar />

          {/* Active by drug and supply length */}
          {byDrug.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Active by drug</CardTitle>
                <p className="text-xs text-muted-foreground">Each ring splits a molecule by supply length. Dollar figures are monthly run rate; hover a segment for its share.</p>
              </CardHeader>
              <CardContent>
                <DrugSupplyRings byDrug={byDrug} />
                {byPlan.length > 0 && (
                  <details className="mt-4 group">
                    <summary className="text-xs text-muted-foreground cursor-pointer select-none hover:text-foreground">
                      By plan ({byPlan.length})
                    </summary>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-3">
                      {byPlan.map(p => (
                        <div key={p.plan} className="flex items-center justify-between border rounded-lg px-3 py-2">
                          <span className="text-xs font-mono truncate mr-2">{p.plan}</span>
                          <span className="text-sm font-bold">{p.count}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </CardContent>
            </Card>
          )}

          {/* Search + Filter */}
          <Card>
            <CardContent className="py-3 px-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="Search by name, email, phone, plan or subscription id..."
                      className="w-full pl-9 pr-8 py-1.5 text-sm border rounded-md bg-background"
                    />
                    {searchInput && (
                      <button type="button" onClick={clearSearch} className="absolute right-2 top-1/2 -translate-y-1/2">
                        <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                      </button>
                    )}
                  </div>
                  <Button type="submit" size="sm">Search</Button>
                </form>
                <div className="flex gap-1">
                  {['all', 'active', 'paused', 'cancelled'].map(s => (
                    <Button
                      key={s}
                      variant={statusFilter === s ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => pickStatus(s)}
                      className="h-8 text-xs"
                    >
                      {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subscriptions Table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {search || statusFilter !== 'all' ? 'Matching subscriptions' : 'Subscriptions'} ({total.toLocaleString()})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {subscriptions.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No subscriptions found</p>
              ) : (
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-medium">Customer</th>
                        <th className="text-left p-3 font-medium">Plan</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-right p-3 font-medium">Price</th>
                        <th className="text-left p-3 font-medium">Billing</th>
                        <th className="text-left p-3 font-medium">Created</th>
                        <th className="text-left p-3 font-medium">Next Billing</th>
                        <th className="text-left p-3 font-medium">State</th>
                        <th className="p-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map(sub => (
                        <tr key={sub._id} className="border-t hover:bg-muted/30">
                          <td className="p-3">
                            <div className="font-medium text-sm">{sub.customer_name || 'Unknown'}</div>
                            {sub.customer_email && (
                              <div className="text-xs text-muted-foreground">{sub.customer_email}</div>
                            )}
                          </td>
                          <td className="p-3 font-mono text-xs">{sub.plan}</td>
                          <td className="p-3"><StatusBadge status={sub.status} /></td>
                          <td className="p-3 text-right font-mono">{formatCurrency(sub.unit_price)}</td>
                          <td className="p-3 text-xs">
                            {sub.billing_period} {sub.billing_period_unit}{sub.billing_period > 1 ? 's' : ''}
                          </td>
                          <td className="p-3 text-xs">{formatDate(sub.created_at)}</td>
                          <td className="p-3 text-xs">
                            {sub.status === 'paused'
                              ? <span className="text-yellow-600">Paused {formatDate(sub.pause_date)}</span>
                              : formatDate(sub.next_billing_at)}
                          </td>
                          <td className="p-3 text-xs">{sub.shipping_state || '-'}</td>
                          <td className="p-3 whitespace-nowrap space-x-3">
                            {sub.patient_id ? (
                              <a
                                href={odinPatientUrl(sub.patient_id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                              >
                                <ExternalLink className="w-3 h-3" /> Odin
                              </a>
                            ) : (
                              <span className="text-xs text-muted-foreground" title="No core patient with this email">No Odin match</span>
                            )}
                            {sub.hyperswitch_customer_id && (
                              <a
                                href={`https://live.hyperswitch.io/dashboard/customers/${sub.hyperswitch_customer_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 whitespace-nowrap"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink className="w-3 h-3" /> HS
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {total > PAGE_SIZE && (
                <div className="flex items-center justify-between pt-3 text-xs text-muted-foreground">
                  <span>
                    {((page - 1) * PAGE_SIZE + 1).toLocaleString()}–{Math.min(page * PAGE_SIZE, total).toLocaleString()} of {total.toLocaleString()}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)} aria-label="Previous page">
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="px-2">Page {page} of {pages}</span>
                    <Button size="sm" variant="outline" disabled={page >= pages || loading} onClick={() => setPage(p => p + 1)} aria-label="Next page">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
